import { useCallback, useRef } from "react";
import type { Wallet as TonConnectWallet } from "@tonconnect/ui-react";
import { useVerifyTonProofMutation } from "../api/wallets.service";
import type { Wallet } from "../../../entities/wallet";

interface UseWalletProofVerificationParams {
  challengeRef: React.MutableRefObject<{ challengeId: string; payload: string; expiresAt: number } | null>;
}

/**
 * Hook to handle wallet proof verification
 * 
 * Responsibilities:
 * - Match the proof to the original challenge
 * - Verify proof using backend API
 * - Track verification state
 */
export function useWalletProofVerification({ challengeRef }: UseWalletProofVerificationParams) {
  const verifyProofMutation = useVerifyTonProofMutation();
  const verifyingRef = useRef<Set<string>>(new Set());

  const verifyProof = useCallback(async (
    wallet: TonConnectWallet,
    dbWallet: Wallet,
    proof: any
  ): Promise<void> => {
    const walletAddress = wallet.account.address;

    // Prevent duplicate verification
    if (verifyingRef.current.has(walletAddress)) {
      console.log("[useWalletProofVerification] Already verifying this wallet, skipping");
      return;
    }

    verifyingRef.current.add(walletAddress);

    try {
      // Only the original challenge was signed by the wallet. Creating another
      // challenge here would always send a mismatched payload to the backend.
      const challenge = challengeRef.current;
      if (!challenge || challenge.payload !== proof.payload ||
          challenge.expiresAt <= Math.floor(Date.now() / 1000)) {
        throw new Error("Wallet proof requires a fresh connection");
      }

      // Normalize chain value based on wallet network
      const normalizedChain = dbWallet.network === "TESTNET" ? -3 : 0;

      // Prepare proof data for verification
      const proofData = {
        challengeId: challenge.challengeId,
        tonProof: {
          address: wallet.account.address,
          chain: normalizedChain,
          state_init: wallet.account.walletStateInit,
          proof: {
            payload: proof.payload,
            signature: proof.signature,
            timestamp: proof.timestamp,
            domain: proof.domain,
          },
        },
      };

      console.log("[useWalletProofVerification] Verifying proof", {
        walletId: dbWallet.id,
        challengeId: proofData.challengeId,
        address: proofData.tonProof.address,
      });

      await verifyProofMutation.mutateAsync({
        walletId: dbWallet.id,
        data: proofData,
      });

      console.log("[useWalletProofVerification] Proof verified successfully", dbWallet.id);
      if (challengeRef.current === challenge) challengeRef.current = null;
    } catch (error) {
      console.error("[useWalletProofVerification] Failed to verify proof", error);
      throw error;
    } finally {
      verifyingRef.current.delete(walletAddress);
    }
  }, [challengeRef, verifyProofMutation]);

  return {
    verifyProof,
    verifyingRef,
    isVerifying: verifyingRef.current.size > 0,
  };
}

